import React, { useState } from 'react';
import { Container, Paper, Box } from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { Login as LoginIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import AuthCaptcha from '../components/forms/AuthCaptcha';

const generateCaptchaChallenge = (length: number = 6) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaChallenge, setCaptchaChallenge] = useState(generateCaptchaChallenge());
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [error, setError] = useState('');
  const { login, state } = useAuth();
  const navigate = useNavigate();

  const refreshCaptcha = (clearError: boolean = true) => {
    setCaptchaChallenge(generateCaptchaChallenge());
    setCaptchaAnswer('');
    if (clearError) {
      setCaptchaError('');
    }
  };

  const handleSubmit = async () => {
    setError('');
    setCaptchaError('');

    if (captchaAnswer.trim().toUpperCase() !== captchaChallenge) {
      setCaptchaError('Captcha does not match. Please try again.');
      refreshCaptcha(false);
      return;
    }

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message);
      refreshCaptcha();
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    void handleSubmit();
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <LoginIcon sx={{ fontSize: 48, color: "var(--app-ink-primary)", mb: 2 }} />
          <Typography variant="h4">लॉगिन / Login</Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <form onSubmit={handleFormSubmit} noValidate>
          <TextField
            fullWidth
            label="ईमेल / Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="पासवर्ड / Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
          />
          <AuthCaptcha
            challengeText={captchaChallenge}
            value={captchaAnswer}
            error={captchaError}
            onChange={(value) => {
              setCaptchaAnswer(value);
              if (captchaError) {
                setCaptchaError('');
              }
            }}
            onRefresh={refreshCaptcha}
          />
          <Button
            type="button"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            onClick={() => void handleSubmit()}
            disabled={state.isLoading || !captchaAnswer.trim()}
          >
            लॉगिन करें / Login
          </Button>
          <Typography align="center" sx={{ mb: 1 }}>
            <Link to="/forgot-password">Forgot Password?</Link>
          </Typography>
          <Typography align="center">
            <Link to="/register">नया अकाउंट बनाएं / Create New Account</Link>
          </Typography>
        </form>
      </Paper>
    </Container>
  );
};

export default Login;
